import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
  jsonLd?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical = '',
  ogType = 'website',
  schema,
  jsonLd
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Get site URL from environment variable
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('last-modified', new Date().toISOString().split('T')[0]);

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', `${siteUrl}${canonical}`, true);
    updateMetaTag('og:locale', 'ja_JP', true);
    updateMetaTag('og:site_name', '赤帽みなとサービス', true);

    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}${canonical}`);

    // Add or update Schema.org JSON-LD
    const schemaData = schema || jsonLd;
    if (schemaData) {
      // Remove existing schema script
      const existingSchema = document.querySelector('script[type="application/ld+json"]');
      if (existingSchema) {
        existingSchema.remove();
      }

      // Create new schema script
      const schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      
      // Process schema to replace placeholder URLs with environment variable
      const processedSchema = JSON.parse(JSON.stringify(schemaData));
      if (processedSchema.url === '') {
        processedSchema.url = siteUrl;
      }
      
      schemaScript.textContent = JSON.stringify(processedSchema);
      document.head.appendChild(schemaScript);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogType, schema, jsonLd]);
}
